import { useState } from 'react';
import useFileUpload from './notifications/useFileUpload';
import { useToast } from '@/components/ui/use-toast';

type FileWithPreview = {
  file: File;
  previewUrl: string;
  id: string;
  uploaded?: boolean;
  uploading?: boolean;
  url?: string;
  error?: string;
};

export const useMultipleFileUpload = () => {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const { uploadFile } = useFileUpload();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    
    const newFiles: FileWithPreview[] = Array.from(selectedFiles).map(file => {
      // Create object URL for preview
      const previewUrl = URL.createObjectURL(file);
      return {
        file,
        previewUrl,
        id: crypto.randomUUID(),
      };
    });
    
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (id: string) => {
    setFiles(prev => {
      const newFiles = prev.filter(file => file.id !== id);
      // Clean up object URLs to avoid memory leaks
      const fileToRemove = prev.find(file => file.id === id);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      return newFiles;
    });
  };

  const uploadFiles = async (bucketName = 'candidate-documents'): Promise<string[]> => {
    if (files.length === 0) return [];
    
    setIsUploading(true);
    
    const uploadPromises = files.map(async (fileData, index) => {
      // Update file state to uploading
      setFiles(prev => prev.map(f => 
        f.id === fileData.id ? { ...f, uploading: true } : f
      ));

      try {
        // Upload to bucket with candidate folder structure
        const url = await uploadFile(fileData.file, bucketName);
        
        if (!url) {
          throw new Error(`Failed to upload ${fileData.file.name}`);
        }
        
        // Update file state to uploaded with URL
        setFiles(prev => prev.map(f => 
          f.id === fileData.id ? { ...f, uploading: false, uploaded: true, url } : f
        ));
        
        return url;
      } catch (error) {
        console.error(`Error uploading file ${fileData.file.name}:`, error);
        
        // Update file state to error
        setFiles(prev => prev.map(f => 
          f.id === fileData.id ? { ...f, uploading: false, error: 'Upload failed' } : f
        ));
        
        toast({
          title: "Upload Error",
          description: `Failed to upload ${fileData.file.name}`,
          variant: "destructive",
        });
        
        return null;
      }
    });
    
    const results = await Promise.all(uploadPromises);
    setIsUploading(false);
    
    return results.filter((url): url is string => url !== null);
  };

  const clearFiles = () => {
    // Clean up object URLs to avoid memory leaks
    files.forEach(file => {
      URL.revokeObjectURL(file.previewUrl);
    });
    setFiles([]);
  };

  const captureImage = async () => {
    // Check if browser supports getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast({
        title: "Error",
        description: "Η συσκευή σας δεν υποστηρίζει πρόσβαση στην κάμερα",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create video and canvas elements
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error("Failed to get canvas context");
      }

      // Get camera stream with better constraints
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      // Connect stream to video element
      video.srcObject = stream;
      video.autoplay = true;
      video.muted = true;
      
      // Wait for video to be ready and playing
      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => {
          // Set canvas size to match video
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          
          // Wait a bit more for the video to start playing
          setTimeout(() => {
            if (video.readyState >= 2) { // HAVE_CURRENT_DATA
              resolve();
            } else {
              reject(new Error("Video not ready"));
            }
          }, 500);
        };
        
        video.onerror = () => reject(new Error("Video loading error"));
      });
      
      // Capture frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Stop all video tracks
      stream.getTracks().forEach(track => track.stop());
      
      // Convert to blob
      const blob = await new Promise<Blob | null>(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.95);
      });
      
      if (!blob) {
        throw new Error("Failed to capture image");
      }

      // Create File from Blob
      const capturedFile = new File(
        [blob], 
        `capture-${Date.now()}.jpg`, 
        { type: 'image/jpeg' }
      );
      
      // Add to files state
      const previewUrl = URL.createObjectURL(capturedFile);
      setFiles(prev => [...prev, {
        file: capturedFile,
        previewUrl,
        id: crypto.randomUUID(),
      }]);
      
      toast({
        title: "Επιτυχία",
        description: "Η φωτογραφία αποθηκεύτηκε",
      });
    } catch (error) {
      console.error("Camera error:", error);
      toast({
        title: "Σφάλμα κάμερας",
        description: "Δεν ήταν δυνατή η πρόσβαση στην κάμερα",
        variant: "destructive",
      });
    }
  };

  return {
    files,
    handleFileSelect,
    handleFileDrop,
    removeFile,
    uploadFiles,
    clearFiles,
    isUploading,
    captureImage
  };
};

export default useMultipleFileUpload;